import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { Card, CardBody, Table } from "reactstrap";
import { formatPriceWithDollar } from "../../../utils/currency";
import { useTranslation } from 'react-i18next';

const getFirstNonEmpty = (...candidates) => {
  for (const value of candidates) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

function formatDateTime(isoString) {
  if (!isoString) return "-";
  try {
    const d = new Date(isoString);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${yy} ${hh}:${mi}`;
  } catch {
    return String(isoString);
  }
}

const SummaryHeader = ({ debitTotal, creditTotal, source }) => {
  const { t } = useTranslation();
  return (
    <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-md-center mb-2">
      <div className="d-flex">
        <div className="mr-4"><strong>{t('glTab.debitTotal')}</strong> {formatPriceWithDollar(debitTotal || 0)}</div>
        <div><strong>{t('glTab.creditTotal')}</strong> {formatPriceWithDollar(creditTotal || 0)}</div>
      </div>
      <div className="mt-1 mt-md-0 ml-md-4"><small className="text-muted">{t('glTab.source')} {source || t('glTab.inventory')}</small></div>
    </div>
  );
};

const GLTab = ({ inventoryData, activeColor = "blue", isTheme2 = false }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(true);

  const batches = useMemo(() => (Array.isArray(inventoryData) ? inventoryData : []), [inventoryData]);

  return (
    <div
      className={classNames("container-fluid px-0 gl-tab", {
        "gl-tab--theme2": isTheme2,
      })}
    >
      {/* <div className="mb-3">
        <Button color="secondary" size="sm" onClick={() => setShowDetails(s => !s)} style={{ borderRadius: 0 }}>
          {showDetails ? t('glTab.hideTransactionDetails') : t('glTab.showTransactionDetails')}
        </Button>
      </div> */}

      {batches.length === 0 && (
        <Card className="mb-3">
          <CardBody className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: 140 }}>
            <div className="text-muted" style={{ fontSize: 14 }}>{t('glTab.noDataToDisplay')}</div>
          </CardBody>
        </Card>
      )}

      {batches.map((batch, index) => (
        <Card key={batch.documentId || batch.id || index} className="mb-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap">
              <div className="mb-2">
                <p className="mb-1"><strong>{t('glTab.batch')}</strong> {batch.batch || ""}</p>
                <p className="mb-1"><strong>{t('glTab.date')}</strong> {formatDateTime(batch.createdAt || batch.date)}</p>
                <p className="mb-1"><strong>{t('glTab.status')}</strong> {batch.gl_status ? batch.gl_status.charAt(0).toUpperCase() + batch.gl_status.slice(1) : ""}</p>
                <p className="mb-1"><strong>{t('glTab.batchDescription')}</strong> {batch.description || "-"}</p>
              </div>
              <SummaryHeader debitTotal={batch.debit_total} creditTotal={batch.credit_total} source={batch.source || t('glTab.inventory')} />
            </div>

            {showDetails && (
              <div className="mt-3">
                <Table bordered responsive size="sm" className="mb-1">
                  <thead className="text-primary">
                    <tr>
                      <th style={{ width: "20%" }}>{t('glTab.account')}</th>
                      <th style={{ width: "40%" }}>{t('glTab.transDescr')}</th>
                      <th style={{ width: "8%" }}>{t('glTab.entryType')}</th>
                      <th style={{ width: "10%" }}>{t('glTab.debit')}</th>
                      <th style={{ width: "10%" }}>{t('glTab.credit')}</th>
                      <th style={{ width: "6%" }}>{t('glTab.location')}</th>
                      <th style={{ width: "6%" }}>{t('glTab.class')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(batch.details || []).map((line, i) => {
                      const account = getFirstNonEmpty(
                        line.account,
                        line.account_name,
                        line.accountNumber && line.accountName
                          ? `${line.accountNumber} ${line.accountName}`
                          : undefined,
                        line.accountNumber,
                      ) || "";
                      const transDescr = getFirstNonEmpty(
                        line.trans_descr,
                        line.description,
                        line.transaction_description,
                      ) || "-";
                      const entryType = getFirstNonEmpty(line.entry_type, line.entryType) || "GJ";
                      const debit = formatPriceWithDollar(getFirstNonEmpty(line.debit, 0));
                      const credit = formatPriceWithDollar(getFirstNonEmpty(line.credit, 0));
                      const location = getFirstNonEmpty(line.location, line.location_code, line.locationName) || "-";
                      const classCode = getFirstNonEmpty(line.class, line.class_code) || "-";

                      return (
                        <tr key={line.id || i}>
                          <td>{account}</td>
                          <td>{transDescr}</td>
                          <td>{entryType}</td>
                          <td>{debit}</td>
                          <td>{credit}</td>
                          <td>{location}</td>
                          <td>{classCode}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  );
};

export default GLTab;